import { Pressable, Text, View } from "react-native";
import { BAND_GLYPH, type WeekCell } from "@heraion/shared";

import type { UiTheme } from "../theme";
import type { GridGestureBundle } from "./useGridGestures";
import type { makeGridStyles } from "./gridStyles";

// PE-27 — one cell of the fused day × week grid. It carries THREE layers that
// used to be spread over two components, and they are deliberately kept on
// separate visual channels so none of them can hide another:
//
//   · fill      = intensity band (per-PROGRAM: the hardest set of that day)
//   · ✓ corner  = placement of the FOCUSED exercise (per-EXERCISE)
//   · ring      = the cursor (which cell the rest of the editor is about)
//
// That separation is the answer to the objection the plan doc raised back when
// these were two grids ("stessa forma, piani-dati diversi"): the data planes
// stay distinct as LAYERS, which is why one grid can carry both without the
// heat and the placement fighting for the same ink.
export function GridCell({
  cell,
  dayIdx,
  week,
  interactive,
  theme,
  styles,
  lit,
  isCursor,
  onPress,
  gesture,
  a11yLabel,
}: {
  cell: WeekCell;
  dayIdx: number;
  week: number;
  /**
   * ⛔ Whether this cell is a control in its OWN right. False on the read-only
   * mount (Home), where the cell means nothing a screen reader needs to stop
   * on — see the comment above the Pressable.
   */
  interactive: boolean;
  theme: UiTheme;
  styles: ReturnType<typeof makeGridStyles>;
  /** The focused exercise is placed here. */
  lit: boolean;
  /** This is the (day, week) the editor's other surfaces are about. */
  isCursor: boolean;
  onPress: () => void;
  /** PE-31 — the placement HOLD and its named screen-reader action, as one
   *  bundle from `useGridGestures`. Empty ⇒ nothing is focused (Home, and the
   *  editor before a pick): the cell keeps its tap and gains no hold. ⛔ Spread,
   *  never destructured into an `onLongPress` prop again — the hold and the
   *  action it names are one contract, and splitting them is how the a11y door
   *  gets wired on one surface and forgotten on the next. */
  gesture: GridGestureBundle;
  a11yLabel: string;
}) {
  const free = !cell.band && cell.sets > 0;
  const c = theme.intensity[cell.band ?? "none"];
  // ⛔ ON HOME THE CELL IS NOT A STOP. Home passes no editor props, so tapping
  // any cell does the one thing tapping the row already did — select the day —
  // and the pinned day button announces it. Left as a button anyway, each cell
  // became its own accessibility stop, taking a Home row from ONE announcement
  // to 1 + weeks (thirteen on a 12-week program), every one of them repeating
  // the day name to say the same thing. That is a regression the fusion
  // introduced silently, on the surface the family actually uses.
  // ⇒ Not interactive ⇒ the press still works (tapping the heat selects the
  // day, as it always did) but the cell is invisible to a screen reader and
  // unreachable by keyboard: the row's own button is the single stop again.
  const a11y = interactive
    ? {
        accessibilityRole: "button" as const,
        accessibilityLabel: a11yLabel,
        accessibilityState: { selected: isCursor },
      }
    : {
        accessibilityElementsHidden: true,
        importantForAccessibility: "no-hide-descendants" as const,
        focusable: false,
      };
  return (
    <Pressable
      {...a11y}
      {...gesture}
      onPress={onPress}
      style={({ pressed }) => [
        styles.gridCellTouch,
        pressed && styles.gridCellPressed,
      ]}
      testID={`grid-cell-${dayIdx}-${week}`}
    >
      <View
        style={[
          styles.gridCell,
          { backgroundColor: c.fill },
          free && styles.gridCellFree,
          isCursor && styles.gridCellCursor,
        ]}
      >
        {/* Absolutely positioned like the band glyph it mirrors, so it costs
            the cell's layout nothing. On a `free` cell the fill is
            transparent, so the band's `on` ink would be a tone chosen for a
            colour that isn't there — the value already switches to plain ink
            for that case, and so does this. */}
        {lit ? (
          <Text
            style={[
              styles.gridCellLit,
              { color: free ? theme.palette.ink : c.on },
            ]}
          >
            ✓
          </Text>
        ) : null}
        {/* Band glyph corner marker — the non-colour cue (W/M/H/X) so the band
            never rides on fill colour alone. */}
        {cell.band ? (
          <Text style={[styles.gridCellBand, { color: c.on }]}>
            {BAND_GLYPH[cell.band]}
          </Text>
        ) : null}
        {cell.value ? (
          <Text style={[styles.gridCellNum, { color: c.on }]}>{cell.value}</Text>
        ) : free ? (
          <Text style={[styles.gridCellNum, styles.gridCellFreeNum]}>
            {`${cell.sets}×`}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}
